import { useState, useEffect } from 'react';
import { getPedsReviewData } from '../../../api/PedsReview';

const usePedsReviewAPI = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [errMessage, setErrorMessage] = useState<string>('');
  const [allReviews, setAllReviews] = useState<Array<any>>([]);
  const [center, setCenter] = useState<string>('all');
  const [searchText, setSearchText] = useState<string>('');
  const [initialized, setInitialized] = useState<boolean>(false);

  // Pagination state
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [totalCount, setTotalCount] = useState<number>(0);

  // Fetch data with optional filters and pagination
  const fetchAllReviews = async (
    overrideCenter?: string,
    overrideSearchText?: string,
    overridePage?: number,
    overridePageSize?: number
  ) => {
    setLoading(true);
    setErrorMessage('');
    try {
      const response = await getPedsReviewData(
        overridePageSize !== undefined ? overridePageSize : pageSize,
        overridePage !== undefined ? overridePage : page,
        overrideCenter !== undefined ? overrideCenter : center,
        overrideSearchText !== undefined ? overrideSearchText : searchText
      );
      if (response?.data?.status === 200) {
        setAllReviews(response.data.body?.result || []);
        setTotalCount(response.data.body?.total || 0);
      } else {
        setErrorMessage('Could not find Peds Review data');
      }
    } catch (err) {
      setErrorMessage('Could not find Peds Review data');
    } finally {
      setLoading(false);
    }
  };

  // Set filters
  const setFilters = (newCenter: string, newSearchText: string) => {
    setCenter(newCenter);
    setSearchText(newSearchText);
    setPage(1); // Reset to first page when filters change
    setInitialized(true); // Mark as initialized when filters are first set
  };

  // Update only center, preserve search text
  const setCenterOnly = (newCenter: string) => {
    setCenter(newCenter);
    setPage(1);
    setInitialized(true);
  };

  // Update only search text, preserve center
  const setSearchOnly = (newSearchText: string) => {
    setSearchText(newSearchText);
    setPage(1);
    setInitialized(true);
  };

  // Clear filters
  const clearFilters = () => {
    setCenter('all');
    setSearchText('');
    setPage(1); // Reset to first page when clearing filters
    // Note: Don't set initialized to false - clearing filters should still trigger fetch
  };

  // Pagination functions
  const changePage = (newPage: number) => {
    setPage(newPage);
    setInitialized(true); // Mark as initialized when page is changed
  };

  const changePageSize = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1); // Reset to first page when page size changes
    setInitialized(true); // Mark as initialized when page size is changed
  };

  // Refetch when filters or pagination change (only after initialization)
  useEffect(() => {
    if (initialized) {
      fetchAllReviews(center, searchText, page, pageSize);
    }
  }, [center, searchText, page, pageSize, initialized]);

  return {
    reviews: allReviews,
    loading,
    errMessage,
    totalCount,
    center,
    searchText,
    initialized,
    setFilters,
    setCenterOnly,
    setSearchOnly,
    clearFilters,
    refetch: fetchAllReviews,
    // Pagination
    page,
    pageSize,
    changePage,
    changePageSize
  };
};

export default usePedsReviewAPI;
